import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {G0Page, GProgrammeRow} from '@/pages/components/audit-program/g-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {indentPaddingLeft, resolveDisplayLevel} from '@/pages/components/audit-program/templates/rowLevel';

export interface G0ProgrammeViewProps {
    header: AuditProgramSectionVO;
    page: G0Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
};
const bodySx = {
    ...cellBorder,
    py: 0.45,
    px: 0.4,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeInputSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.5,
    py: '3px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CompactInput = memo(function CompactInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeInputSx, textAlign: align}}
        />
    );
});

const SectionRow = memo(function SectionRow({text}: {text: string}) {
    return (
        <TableRow>
            <TableCell sx={bodySx} colSpan={7}>
                <Typography variant="body2" fontWeight={700} sx={{py: 0.25}}>
                    {text}
                </Typography>
            </TableCell>
        </TableRow>
    );
});

const DataRow = memo(function DataRow({
    row,
    testRequired,
    resultsOk,
    schRef,
    comments,
    initials,
    onChange,
}: {
    row: GProgrammeRow;
    testRequired: string;
    resultsOk: string;
    schRef: string;
    comments: string;
    initials: string;
    onChange: (key: string, value: string) => void;
}) {
    const pl = indentPaddingLeft(resolveDisplayLevel(row));
    const onTest = useCallback((v: string) => onChange(`${row.id}.testRequired`, v), [onChange, row.id]);
    const onResults = useCallback((v: string) => onChange(`${row.id}.resultsOk`, v), [onChange, row.id]);
    const onSch = useCallback((v: string) => onChange(`${row.id}.schRef`, v), [onChange, row.id]);
    const onComments = useCallback((v: string) => onChange(`${row.id}.comments`, v), [onChange, row.id]);
    const onInitials = useCallback((v: string) => onChange(`${row.id}.initials`, v), [onChange, row.id]);

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" fontWeight={600} sx={{pl}}>
                    {row.num}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.4, pl}}>
                    {row.text}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={testRequired} onChange={onTest} align="center"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={resultsOk} onChange={onResults} align="center"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={schRef} onChange={onSch} align="center"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={comments} onChange={onComments}/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CompactInput value={initials} onChange={onInitials} align="center"/>
            </TableCell>
        </TableRow>
    );
});

/** G0 audit programme: Test Y/N · Results Y/N · Sch ref · Comments · Initials. */
export default function G0ProgrammeView({
    header,
    page,
    refCode = 'G0',
    values,
    onChange,
}: G0ProgrammeViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {page.title}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 980, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 48}}/>
                        <col/>
                        <col style={{width: 88}}/>
                        <col style={{width: 100}}/>
                        <col style={{width: 72}}/>
                        <col style={{width: 160}}/>
                        <col style={{width: 110}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">Test required Y/N</TableCell>
                            <TableCell sx={headSx} align="center">Results satisfactory Y/N</TableCell>
                            <TableCell sx={headSx} align="center">Sch ref</TableCell>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                            <TableCell sx={headSx} align="center">Initials and date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {page.rows.map((row) => {
                            if (row.kind === 'section') {
                                return <SectionRow key={row.id} text={row.text}/>;
                            }
                            if (row.kind === 'note') {
                                const label = row.num ? `${row.num}. ` : '';
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={7}>
                                            <Typography variant="body2" sx={{py: 0.25, fontStyle: 'italic', lineHeight: 1.4}}>
                                                {label}{row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            return (
                                <DataRow
                                    key={row.id}
                                    row={row}
                                    testRequired={values[`${row.id}.testRequired`] ?? ''}
                                    resultsOk={values[`${row.id}.resultsOk`] ?? ''}
                                    schRef={values[`${row.id}.schRef`] ?? ''}
                                    comments={values[`${row.id}.comments`] ?? ''}
                                    initials={values[`${row.id}.initials`] ?? ''}
                                    onChange={stableOnChange}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
